import { type ReactNode, useEffect, useRef } from 'react';

import { Button, Logo } from '@opositandopn/ui';
import classnames from 'classnames';
import { IoCloudOfflineOutline, IoSync } from 'react-icons/io5';

import { BaseComponentProps } from '../../types/base-component.types';

import styles from './BootError.module.scss';

type BootErrorProps = BaseComponentProps & {
  title?: string;
  message?: string;
  /** Overrides the default cloud-offline mark when the cause is not the connection. */
  icon?: ReactNode;
  /** What the retry button does. Omitted only when there is nothing to retry. */
  onRetry?: () => void;
  retryLabel?: string;
  retrying?: boolean;
};

/**
 * What the app shows when it cannot finish starting. The boot chain is a stack of
 * gates that each render the splash while they wait, so a step that fails without
 * saying so leaves the logo on screen for good — indistinguishable from one that is
 * still loading. This is the other outcome: it says the start failed and offers the
 * way out, which is to try again.
 */
function BootError({
  className,
  title,
  message,
  icon,
  onRetry,
  retryLabel = 'Reintentar',
  retrying = false,
}: BootErrorProps) {
  const retryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // There is exactly one thing to do on this screen and nothing above it to read
    // past, so putting the caret on it saves a keyboard user the tab and tells a
    // screen reader user what their options are.
    retryRef.current?.focus();
  }, []);

  return (
    <div
      className={classnames(styles['main'], className)}
      role="alert"
      aria-live="assertive"
      data-testid="ui-boot-error"
    >
      <Logo className={styles['logo']} variant="logo-h" />

      <div className={styles['panel']}>
        {/* Decorative: the title says the same thing in words. */}
        <span className={styles['badge']} aria-hidden="true">
          {icon ?? <IoCloudOfflineOutline />}
        </span>

        <h1 className={styles['title']}>
          {title ?? '¡Ups! No hemos podido entrar'}
        </h1>

        <p className={styles['message']}>
          {message ??
            'No hemos podido conectar con el servidor. Comprueba tu conexión e inténtalo de nuevo.'}
        </p>

        {onRetry !== undefined && (
          <Button
            ref={retryRef}
            className={styles['action']}
            variant="primary"
            onClick={onRetry}
            loading={retrying}
            suffixIcon={<IoSync />}
          >
            {retryLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

export { BootError };
